/** Format a value for `<input type="date">` (calendar date, no UTC day shift). */
export function formatDateForInput(
  value: string | Date | null | undefined,
): string {
  if (value == null || value === "") return "";

  if (value instanceof Date) {
    if (Number.isNaN(value.getTime())) return "";
    return toLocalYmd(value);
  }

  const s = String(value).trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;

  // API date-times: keep the calendar date from the string (admin picked YYYY-MM-DD).
  const datePart = s.match(/^(\d{4}-\d{2}-\d{2})/);
  if (datePart) return datePart[1];

  const parsed = new Date(s);
  if (Number.isNaN(parsed.getTime())) return "";
  return toLocalYmd(parsed);
}

/**
 * Format for `<input type="datetime-local">` (YYYY-MM-DDTHH:mm).
 * Uses calendar components from the API string so UK wall-clock times are not shifted.
 */
export function formatDateTimeForInput(
  value: string | Date | null | undefined,
): string {
  if (value == null || value === "") return "";

  if (value instanceof Date) {
    if (Number.isNaN(value.getTime())) return "";
    return `${toLocalYmd(value)}T${toLocalHm(value)}`;
  }

  const s = String(value).trim();
  const withTime = s.match(/^(\d{4}-\d{2}-\d{2})[T ](\d{2}):(\d{2})/);
  if (withTime) return `${withTime[1]}T${withTime[2]}:${withTime[3]}`;

  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return `${s}T00:00`;

  const datePart = formatDateForInput(s);
  return datePart ? `${datePart}T00:00` : "";
}

/** Today's date in local timezone for date inputs. */
export function formatTodayForDateInput(): string {
  return toLocalYmd(new Date());
}

/** Yesterday (local) — default early bird end date for new events. */
export function formatYesterdayForDateInput(): string {
  const d = new Date();
  d.setDate(d.getDate() - 1);
  return toLocalYmd(d);
}

function toLocalYmd(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function toLocalHm(d: Date): string {
  const h = String(d.getHours()).padStart(2, "0");
  const min = String(d.getMinutes()).padStart(2, "0");
  return `${h}:${min}`;
}
